/* eslint-disable consistent-return */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { playAttentionSound } from '@vlabs/shared/utils/playAttentionSound';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { selectAppConfig } from '@vlapps/point/src/app/store/selectors';
import {
  selectNotifyEvents,
  selectViewedNotifyIds,
  selectShownNotifyEventIds,
} from '@vlabs/pages/point/workspace/selectors';
import { setShownNotifyIds, setViewedNotifyIds } from '@vlabs/pages/point/workspace/store';

import { routes } from '@vlapps/point/src/app/routes';

import { getArrFromLocalStorage, setArrToLocalStorage } from '@vlabs/monokit/shared/lib/localStorage/index.ts';
import { filterEventsByNotifications, enrichDataWithListOptions } from '@vlabs/monokit/shared/ui/notification-provider/index.ts';
import { NotificationToast } from '@vlabs/monokit/shared/ui/notification-toast/index.ts';

import { enrichDataWithUIRecipients } from './enrichDataWithUIRecipients';
import { filterVAByNotifications } from './filterVAByNotifications';
import st from './NotificationProvider.module.sass';

function NotificationProviderComponent({
  notifyEvents,
  viewedNotifyIds,
  shownNotifyEventIds,
  setShownNotify,
  setViewedNotify,
  uiNotificationsEnabled,
  children,
}) {
  const { pathname } = useLocation();
  const [latestId, setLatestId] = useState();
  const [notifications, setNotifications] = useState();
  const [listOptions, setListOptions] = useState();
  const [enrichedData, setEnrichedData] = useState();

  const [pausedNotificationArr, setPausedNotificationArr] = useState([]);

  const checkPausedNotification = () => {
    const filtered = [...pausedNotificationArr].filter((notification) => notification.time > Date.now());
    if (filtered.length !== pausedNotificationArr.length) setPausedNotificationArr(filtered);
  };

  useEffect(() => {
    setPausedNotificationArr(getArrFromLocalStorage('pausedNotifications'));
  }, [getArrFromLocalStorage('pausedNotifications').length]);

  useEffect(() => {
    setArrToLocalStorage('pausedNotifications', pausedNotificationArr);
  }, [pausedNotificationArr]);

  useEffect(() => {
    if (!pausedNotificationArr.length) return;
    const interval = setInterval(checkPausedNotification, 60000);
    return () => clearInterval(interval);
  }, [pausedNotificationArr.length]);

  // Получаем список включённых настроек уведомлений notifications и списки listOptions
  useEffect(() => {
    Promise.all([
      apiContainer.notifierClient.filters.getAll(),
      // FIXME: почему мы тут делаем запрос lists.getAll?
      apiContainer.lunaClient.lists.getAll(),
    ]).then(([{ data: filtersData }, { data: lists }]) => {
      const vaNotifications = getArrFromLocalStorage('VANotificationsArr');
      const filteredEventData = filtersData.filter((notification) => notification.ui.enable === true);
      const filteredVAData = vaNotifications.filter((notification) => notification.ui.enable === true);
      setNotifications([...filteredEventData, ...filteredVAData]);
      // FIXME: мы получаем обычные списки а ниже работаем с ними как с опшинами..?
      setListOptions(lists);
    });
  }, []);

  const disabledUntil = Number(localStorage.getItem('offUINotification')) || 0;

  const onCardClick = (event) => {
    const id = event?.event_id || event?.event?.event_id;
    setViewedNotify(id);
    window.open(`${routes.events.latest}?id=${id}`, '_blank', 'noopener,noreferrer');
  };

  // Определяем список непросмотренных событий unseenEvents
  const unseenEvents = useMemo(() => {
    if (!notifyEvents.length) return [];
    return notifyEvents.filter((event) => {
      const id = event.event_id || event.event?.event_id;
      return !viewedNotifyIds?.includes(id) && !shownNotifyEventIds?.includes(id);
    });
  }, [notifyEvents, viewedNotifyIds, shownNotifyEventIds]);

  // Получаем enrichedData: список непросмотренных событий unseenEvents, обогащённый списками listOptions
  useEffect(() => {
    let isCancelled = false;

    const fetchEnriched = async () => {
      if (!unseenEvents?.length || !listOptions?.length) setEnrichedData([]);
      const result = await enrichDataWithListOptions(unseenEvents, listOptions);
      if (!isCancelled) setEnrichedData(result);
    };
    fetchEnriched();

    return () => {
      isCancelled = true;
    };
  }, [unseenEvents, listOptions]);

  // Получаем matchedEvents: список событий для уведомлений (отфильтрованный enrichedData фильтрам из notifications)
  const matchedEvents = useMemo(() => {
    if (!notifications?.length || !enrichedData?.length) return [];
    const notificationsVAArr = notifications.filter((n) => n.filters?.type) || [];
    const notificationsEventArr = notifications.filter((n) => !n.filters?.type) || [];
    return enrichedData.filter((e) => {
      if (e.event_type) {
        if (!notificationsVAArr.length) return false;
        return notificationsVAArr.some((n) => filterVAByNotifications(e, n));
      }
      if (!notificationsEventArr.length) return false;
      return notificationsEventArr.some((n) => filterEventsByNotifications(e, n));
    });
  }, [notifications, enrichedData]);

  // Получаем matchedEventsWithRecipients: список событий для уведомлений, обогащённый boolean настройками попапа и звука из notifications
  const matchedEventsWithRecipients = useMemo(
    () => enrichDataWithUIRecipients(matchedEvents, notifications),
    [matchedEvents, notifications],
  );

  // Функция запуска уведомлений: уведомление о событиях из matchedEventsWithRecipients согласно настроек попапа и звука
  const showNotification = () => {
    if (
      pathname === routes.events.latest
    || !matchedEventsWithRecipients?.length
    || !uiNotificationsEnabled
    || Date.now() < disabledUntil
    ) return;

    const lastEvent = matchedEventsWithRecipients.at(-1);
    const lastId = lastEvent?.event_id || lastEvent?.event?.event_id;

    let shouldPlaySound = false;

    matchedEventsWithRecipients.forEach((event) => {
      const id = event.event_id || event.event?.event_id;
      const popupEnabled = !event.event_type || event.uiRecipients?.popup;
      const soundEnabled = !event.event_type || event.uiRecipients?.sound;

      if (popupEnabled) {
        toast.error(
          <NotificationToast
            event={event}
            onClick={() => onCardClick(event)}
          />,
          { position: 'top-right', className: st.NotificationToast },
        );
        setShownNotify(id);
      }
      if (soundEnabled) shouldPlaySound = true;
    });

    if (shouldPlaySound && lastId && lastId !== latestId) {
      playAttentionSound();
      setLatestId(lastId);
    }
  };

  useEffect(() => {
    showNotification();
  }, [
    matchedEvents,
    pathname,
    uiNotificationsEnabled,
  ]);

  return children;
}

export const NotificationProvider = connect(
  (state) => ({
    notifyEvents: selectNotifyEvents(state),
    viewedNotifyIds: selectViewedNotifyIds(state),
    shownNotifyEventIds: selectShownNotifyEventIds(state),
    uiNotificationsEnabled: selectAppConfig(state).uiNotificationsEnabled,
  }),
  (dispatch) => ({
    setShownNotify: (id) => dispatch(setShownNotifyIds({ id })),
    setViewedNotify: (id) => dispatch(setViewedNotifyIds({ id })),
  }),
)(NotificationProviderComponent);
